// The local store: entities, the outbox, dead letters and the sync state, behind one small
// transactional interface. Two implementations: IndexedDB in the browser (idb.ts) and memory (here,
// for unit tests and browsers without IndexedDB). The sync logic (ops.ts) is written once against Tx,
// and only ever awaits Tx calls inside a transaction, which is what IndexedDB requires.
import type { Collection, DeadRow, LocalEntity, OutboxRow, SyncMeta } from "./types";

export interface Tx {
  getMeta(): Promise<SyncMeta | undefined>;
  putMeta(m: SyncMeta): void;
  getEntity(collection: Collection, id: string): Promise<LocalEntity | undefined>;
  putEntity(e: LocalEntity): void;
  deleteEntity(collection: Collection, id: string): void;
  entities(collection?: Collection): Promise<LocalEntity[]>;
  /** Every queued mutation, in insertion order. */
  outbox(): Promise<OutboxRow[]>;
  outboxFor(collection: Collection, id: string): Promise<OutboxRow[]>;
  /** Adds a row (no seq) or replaces one (with seq). */
  putOutbox(row: Omit<OutboxRow, "seq"> & { seq?: number }): void;
  deleteOutbox(seq: number): void;
  putDead(row: DeadRow): void;
  dead(): Promise<DeadRow[]>;
  clear(): void;
}

export interface Store {
  /** Runs `fn` in one transaction: everything it writes commits together or not at all. */
  tx<T>(mode: "readonly" | "readwrite", fn: (tx: Tx) => Promise<T>): Promise<T>;
  /** Deletes the whole store (signing out). */
  destroy(): Promise<void>;
}

const k = (c: string, id: string) => `${c}\u0000${id}`;

/** A store in memory: the same semantics as IndexedDB, transactions applied all at once. */
export function memoryStore(): Store {
  let meta: SyncMeta | undefined;
  let entities = new Map<string, LocalEntity>();
  let outbox = new Map<number, OutboxRow>();
  let dead: DeadRow[] = [];
  let seq = 0;
  let queue: Promise<unknown> = Promise.resolve();

  return {
    tx(mode, fn) {
      const run = async () => {
        // Work on copies; commit only if fn resolves.
        let m = meta && structuredClone(meta);
        const ents = new Map([...entities].map(([key, v]) => [key, structuredClone(v)]));
        const out = new Map([...outbox].map(([key, v]) => [key, structuredClone(v)]));
        let dl = [...dead];
        let s = seq;
        const tx: Tx = {
          getMeta: async () => m && structuredClone(m),
          putMeta: (v) => void (m = structuredClone(v)),
          getEntity: async (c, id) => {
            const e = ents.get(k(c, id));
            return e && structuredClone(e);
          },
          putEntity: (e) => void ents.set(k(e.collection, e.id), structuredClone(e)),
          deleteEntity: (c, id) => void ents.delete(k(c, id)),
          entities: async (c) => [...ents.values()].filter((e) => !c || e.collection === c).map((e) => structuredClone(e)),
          outbox: async () => [...out.values()].sort((a, b) => a.seq - b.seq).map((r) => structuredClone(r)),
          outboxFor: async (c, id) =>
            [...out.values()]
              .filter((r) => r.collection === c && r.id === id)
              .sort((a, b) => a.seq - b.seq)
              .map((r) => structuredClone(r)),
          putOutbox: (row) => {
            const n = row.seq ?? ++s;
            out.set(n, structuredClone({ ...row, seq: n }));
          },
          deleteOutbox: (n) => void out.delete(n),
          putDead: (row) => void dl.push(structuredClone(row)),
          dead: async () => dl.map((r) => structuredClone(r)),
          clear: () => {
            m = undefined;
            ents.clear();
            out.clear();
            dl = [];
          },
        };
        const result = await fn(tx);
        if (mode === "readwrite") {
          meta = m;
          entities = ents;
          outbox = out;
          dead = dl;
          seq = s;
        }
        return result;
      };
      const p = queue.then(run, run);
      queue = p.catch(() => undefined);
      return p;
    },
    async destroy() {
      meta = undefined;
      entities = new Map();
      outbox = new Map();
      dead = [];
    },
  };
}
